import React, { useState, useEffect, useCallback, useRef } from 'react';
import delivgateApi from '../services/delivgateApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function minutesAgo(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
}

function formatMinutes(min) {
  if (min === null) return '-';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m > 0 ? m + 'min' : ''}`;
}

function platformLabel(p) {
  const map = { ninetynine: '99food', ifood: 'iFood', whatsapp: 'WhatsApp', direct: 'Direto' };
  return map[p] ?? p;
}

function platformColor(p) {
  const map = { ninetynine: '#E8003D', ifood: '#EA1D2C', whatsapp: '#25D366', direct: '#6B7280' };
  return map[p] ?? '#6B7280';
}

const STATUS_LABEL = {
  NEW: 'Novo',
  CONFIRMED: 'Confirmado',
  PREPARING: 'Preparando',
  READY: 'Pronto',
  DISPATCHED: 'Despachado',
};

const STATUS_COLOR = {
  NEW: '#F59E0B',
  CONFIRMED: '#3B82F6',
  PREPARING: '#8B5CF6',
  READY: '#10B981',
  DISPATCHED: '#EF4444',
};

const DRIVER_STATUS_LABEL = {
  DISPONIVEL: 'Disponível',
  EM_ROTA: 'Em rota',
  AGUARDANDO_CLIENTE: 'Aguardando cliente',
  RETORNANDO: 'Retornando',
};

const DRIVER_STATUS_COLOR = {
  DISPONIVEL: '#10B981',
  EM_ROTA: '#F59E0B',
  AGUARDANDO_CLIENTE: '#EF4444',
  RETORNANDO: '#3B82F6',
};

// ─── Subcomponentes ───────────────────────────────────────────────────────────

function StatusCard({ label, count, color }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      padding: '16px 20px',
      borderLeft: `5px solid ${color}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      minWidth: 110,
      flex: 1,
    }}>
      <div style={{ fontSize: 32, fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{label}</div>
    </div>
  );
}

function DriverCard({ driver }) {
  const color = DRIVER_STATUS_COLOR[driver.status] ?? '#6B7280';
  const label = DRIVER_STATUS_LABEL[driver.status] ?? driver.status;
  const minLogged = minutesAgo(driver.loggedInAt);

  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      padding: '14px 16px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 160,
    }}>
      <div style={{ fontWeight: 600, fontSize: 15, color: '#111' }}>{driver.nome}</div>
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: color + '20',
        color,
        borderRadius: 20,
        padding: '3px 10px',
        fontSize: 12,
        fontWeight: 600,
        width: 'fit-content',
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }} />
        {label}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#111' }}>{driver.deliveriesToday}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>entregas hoje</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{formatMinutes(minLogged)}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>logado há</div>
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order }) {
  const elapsed = minutesAgo(order.createdAt);
  const statusColor = STATUS_COLOR[order.status] ?? '#6B7280';
  const pColor = platformColor(order.platform);
  const isLate = elapsed !== null && elapsed > 45;

  return (
    <tr style={{ borderBottom: '1px solid #F3F4F6', background: isLate ? '#FFF7ED' : 'transparent' }}>
      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#111', whiteSpace: 'nowrap' }}>
        #{order.orderIndex ?? order.platformOrderId?.slice(-6) ?? '-'}
      </td>
      <td style={{ padding: '10px 12px' }}>
        <span style={{
          background: pColor + '15',
          color: pColor,
          borderRadius: 6,
          padding: '2px 8px',
          fontSize: 12,
          fontWeight: 600,
        }}>
          {platformLabel(order.platform)}
        </span>
      </td>
      <td style={{ padding: '10px 12px', color: '#374151', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {order.customerName}
      </td>
      <td style={{ padding: '10px 12px', color: '#6B7280', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {order.addressFormatted ?? order.addressNeighborhood ?? '-'}
      </td>
      <td style={{ padding: '10px 12px' }}>
        <span style={{
          background: statusColor + '20',
          color: statusColor,
          borderRadius: 6,
          padding: '3px 10px',
          fontSize: 12,
          fontWeight: 600,
        }}>
          {STATUS_LABEL[order.status] ?? order.status}
        </span>
      </td>
      <td style={{ padding: '10px 12px', fontWeight: 600, color: isLate ? '#EF4444' : '#374151', whiteSpace: 'nowrap' }}>
        {formatMinutes(elapsed)}
      </td>
      <td style={{ padding: '10px 12px', color: '#6B7280', fontSize: 13 }}>
        {order.driverName ?? '-'}
      </td>
      <td style={{ padding: '10px 12px', color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
        R$ {((order.total ?? 0) / 100).toFixed(2)}
      </td>
    </tr>
  );
}

// ─── Dashboard principal ──────────────────────────────────────────────────────

const TABS = ['Pedidos', 'Motoboys'];

export default function WapdDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Pedidos');
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await delivgateApi.get('/wapd/dashboard');
      setData(res.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (e) {
      setError('Erro ao carregar dados. Verifique a conexão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    intervalRef.current = setInterval(fetchDashboard, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchDashboard]);

  const statusOrder = ['NEW', 'CONFIRMED', 'PREPARING', 'READY', 'DISPATCHED'];
  const counts = data?.statusCounts ?? {};
  const totalActive = Object.values(counts).reduce((s, n) => s + n, 0);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9FAFB',
      fontFamily: '"DM Sans", sans-serif',
    }}>
      {/* Header */}
      <div style={{
        background: '#B55219',
        color: '#fff',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>Delivery</span>
          <span style={{
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 20,
            padding: '2px 12px',
            fontSize: 13,
            fontWeight: 600,
          }}>
            {totalActive} ativos
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {lastUpdate && (
            <span style={{ fontSize: 12, opacity: 0.8 }}>
              Atualizado {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={fetchDashboard}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              padding: '6px 14px',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            ↻ Atualizar
          </button>
          <a href="/" style={{ color: '#fff', fontSize: 13, opacity: 0.8, textDecoration: 'none' }}>← Sair</a>
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {/* Cards de status */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          {statusOrder.map((s) => (
            <StatusCard
              key={s}
              label={STATUS_LABEL[s]}
              count={counts[s] ?? 0}
              color={STATUS_COLOR[s]}
            />
          ))}
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '12px 16px',
            color: '#B91C1C',
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #E5E7EB' }}>
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                background: 'none',
                border: 'none',
                padding: '10px 20px',
                fontSize: 15,
                fontWeight: tab === t ? 700 : 500,
                color: tab === t ? '#B55219' : '#6B7280',
                borderBottom: tab === t ? '2px solid #B55219' : '2px solid transparent',
                marginBottom: -2,
                cursor: 'pointer',
              }}
            >
              {t}
              {t === 'Pedidos' && <span style={{ marginLeft: 6, fontSize: 12, background: '#F3F4F6', borderRadius: 10, padding: '1px 7px' }}>{totalActive}</span>}
              {t === 'Motoboys' && <span style={{ marginLeft: 6, fontSize: 12, background: '#F3F4F6', borderRadius: 10, padding: '1px 7px' }}>{data?.drivers?.length ?? 0}</span>}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>Carregando...</div>
        )}

        {!loading && tab === 'Motoboys' && (
          <div>
            {(!data?.drivers || data.drivers.length === 0) ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>
                Nenhum motoboy logado no momento.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                {data.drivers.map((d) => (
                  <DriverCard key={d.id} driver={d} />
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && tab === 'Pedidos' && (
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            {(!data?.activeOrders || data.activeOrders.length === 0) ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>
                Nenhum pedido ativo no momento.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                      {['#', 'Plataforma', 'Cliente', 'Endereço', 'Status', 'Tempo', 'Motoboy', 'Total'].map((h) => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6B7280', whiteSpace: 'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.activeOrders.map((o) => (
                      <OrderRow key={o.id} order={o} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
